import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, Loader2, ChevronDown } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { submitContact } from "../../lib/api";
import { track } from "../../lib/analytics";
import { Reveal } from "./Reveal";
import { SocialIcons } from "./SocialIcons";
import { SectionBg } from "./SectionBg";

const SUBJECTS = ["Court métrage / Documentaire", "Film de sensibilisation", "Atelier vidéo / Formation", "Mariage / Baptême", "Événement", "Vidéo pro (asso/entreprise/collectivité)", "Karams Festival", "Adhésion / Bénévolat", "Autre"];

export const Contact = () => {
  const { content } = useContent();
  const c = content.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Merci de remplir les champs requis."); return; }
    setLoading(true);
    try {
      await submitContact(form);
      track("form", { path: "contact" });
      toast.success("Message envoyé — nous vous répondrons très vite.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch { toast.error("Une erreur est survenue. Réessayez."); }
    finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-white/20 focus:border-electric outline-none py-4 text-white placeholder:text-white/30 transition-colors duration-300 text-base";
  const ss = c.subjectStyle || {}; // admin-configurable colours for the Sujet dropdown

  return (
    <section id="contact" className="relative overflow-hidden bg-[#050505] py-24 md:py-40 border-t border-white/10" data-testid="contact-section">
      <SectionBg id="contact" />
      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-electric" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-electric">Contact</span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-10">
              Racontons<br />votre <span className="italic text-electric">histoire.</span>
            </h2>
            <p className="text-secondary2 font-light leading-relaxed max-w-md mb-12">
              Un projet, une formation, une envie de rejoindre l'association ou de participer au Karams Festival ? Écrivez-nous — tout commence par une conversation.
            </p>
            <div className="space-y-5">
              <a data-testid="contact-phone" href={`tel:${c.phone}`} className="flex items-center gap-4 text-secondary2 hover:text-electric transition-colors">
                <Phone size={18} className="text-electric" /> <span className="text-lg">{c.phone}</span>
              </a>
              <a data-testid="contact-email" href={`mailto:${c.email}`} className="flex items-center gap-4 text-secondary2 hover:text-electric transition-colors">
                <Mail size={18} className="text-electric" /> <span className="text-lg">{c.email}</span>
              </a>
              <div className="flex items-center gap-4 pt-4">
                <SocialIcons socials={c.socials} />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input data-testid="contact-name-input" className={field} placeholder="Nom *" value={form.name} onChange={update("name")} />
                <input data-testid="contact-email-input" type="email" className={field} placeholder="Email *" value={form.email} onChange={update("email")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input data-testid="contact-phone-input" className={field} placeholder="Téléphone" value={form.phone} onChange={update("phone")} />
                <div className="relative">
                  <select data-testid="contact-service-select" className={`${field} appearance-none cursor-pointer pr-9`}
                    style={{ color: ss.textColor || undefined, borderBottomColor: ss.lineColor || undefined }}
                    value={form.service} onChange={update("service")}>
                    <option value="" className="bg-[#181818]">Sujet — cliquez pour choisir</option>
                    {SUBJECTS.map((s) => <option key={s} value={s} className="bg-[#181818]">{s}</option>)}
                  </select>
                  <ChevronDown size={18} data-testid="contact-service-chevron" className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-electric" style={{ color: ss.arrowColor || undefined }} />
                </div>
              </div>
              <textarea data-testid="contact-message-input" rows={4} className={`${field} resize-none`} placeholder="Votre message *" value={form.message} onChange={update("message")} />
              <button data-testid="contact-submit-btn" data-track="Envoyer le message" type="submit" disabled={loading}
                className="group inline-flex items-center gap-3 px-9 py-4 bg-surface text-white border border-white/10 rounded-full text-[13px] tracking-[0.12em] uppercase font-medium hover:border-electric hover:text-electric hover:glow-electric transition-[color,border-color,box-shadow] duration-400 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                Envoyer le message
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
